import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import AppRankingRewardTabs from './AppRankingRewardTabs'
import { APP_RANKING_BANNER_EXPOSE_REGION_LABELS } from './appRankingBannerDraftUtils'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
    },
}

const RANKING_BANNER_LIST_IMAGE_SX = {
    width: 72,
    height: 44,
    objectFit: 'contain',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: '4px',
    display: 'block',
}

const APP_RANKING_BANNER_TABLE_COLUMNS = [
    { width: 70 },
    { width: 110 },
    { width: 130 },
    { width: 90 },
    { width: 120 },
    { width: 220 },
    { width: 200 },
    { width: 200 },
    { width: 100 },
    { width: 100 },
    { width: 120 },
]

const APP_RANKING_BANNER_TABLE_COLUMN_COUNT = APP_RANKING_BANNER_TABLE_COLUMNS.length

const REWARD_TYPE_LABELS = {
    none: '전시만',
    point: '포인트',
    gifticon: '기프티콘',
}

// 순위는 범위 규칙(2~5위)도 있어서 시작·끝을 함께 보여준다.
const formatRankRange = (row) => {
    const from = Number(row.rank)
    const to = row.rankTo === null || row.rankTo === undefined || row.rankTo === '' ? from : Number(row.rankTo)

    return from === to ? `${from}위` : `${from}~${to}위`
}

const formatReward = (row) => {
    const label = REWARD_TYPE_LABELS[row.rewardType] || REWARD_TYPE_LABELS.none

    if (row.rewardType === 'point') {
        return `${label} ${Number(row.pointAmount || 0).toLocaleString()}P`
    }

    if (row.rewardType === 'gifticon') {
        const name = `${label} · ${row.goodsName || row.goodsCode || '-'}`
        // 기프티콘을 못 보냈을 때 대신 줄 수 있는 포인트(권장 소비자가 × 3).
        return row.convertPoint ? `${name} (${Number(row.convertPoint).toLocaleString()}P)` : name
    }

    return label
}

const formatApplyPeriod = (row) => {
    if (!row.applyFrom && !row.applyTo) {
        return '기간 제한 없음'
    }

    return `${row.applyFrom || '-'} ~ ${row.applyTo || '-'}`
}

const APP_RANKING_BANNER_TABLE_MIN_WIDTH = APP_RANKING_BANNER_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_RANKING_BANNER_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_RANKING_BANNER_NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

export default function AppRankingBanners({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const silentListRefreshRef = useRef(false)
    const deleteInFlightRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appRankingBanner/getList', {
                page: curPage, offset: offset, adminId: adminId, role: role, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 랭킹경품 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 랭킹경품 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const hasActiveFilters = Boolean(
        searchQuery ||
        searchSelect.searchType
    )

    // 목록 상태에 맞는 빈 화면을 한 곳에서 결정한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 랭킹경품 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button variant="outlined" size="small" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 랭킹경품이 없습니다."
                    description="타이틀 검색어를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 App 랭킹경품이 없습니다."
                description="App 랭킹경품을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/appRankingBanner/delete', { rankingBannerId: id })
                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)
                    if (curPage > 1 && params.length === 1) {
                        setCurPage(curPage - 1)
                    } else {
                        await getData(false)
                    }
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('App 랭킹경품을 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }
        deleteData()
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>App 랭킹경품 리스트</Title>
                    <AppRankingRewardTabs current="rules" disabled={actionLoading} />
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="타이틀"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appRankingBanner',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_RANKING_BANNER_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_RANKING_BANNER_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-ranking-banner-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>노출상태</TableCell>
                                <TableCell>구분</TableCell>
                                <TableCell>순위</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>경품명</TableCell>
                                <TableCell>지급 경품</TableCell>
                                <TableCell>적용 기간</TableCell>
                                <TableCell>노출 국가</TableCell>
                                <TableCell>적용 중</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.rankingBannerId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>{row.status === 1 ? '노출' : '미노출'}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>{row.type === 1 ? '러닝 주간' : row.type === 2 ? '러닝 월간' : row.type === 3 ? '만보기 주간' : '만보기 월간'}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>{formatRankRange(row)}</TableCell>
                                        <TableCell>
                                            {row.image ? (
                                                <img
                                                    src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`}
                                                    alt=""
                                                    style={RANKING_BANNER_LIST_IMAGE_SX}
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_TEXT_CELL_SX} title={row.title || ''}>{row.title || '-'}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_TEXT_CELL_SX} title={formatReward(row)}>{formatReward(row)}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>{formatApplyPeriod(row)}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>{APP_RANKING_BANNER_EXPOSE_REGION_LABELS[row.exposeRegion] || APP_RANKING_BANNER_EXPOSE_REGION_LABELS.all}</TableCell>
                                        <TableCell sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>{row.isApplyingNow === 1 ? '적용 중' : '-'}</TableCell>
                                        <TableCell align="right" sx={APP_RANKING_BANNER_NOWRAP_CELL_SX}>
                                            <ListActionGroup disabled={actionLoading}>
                                                <EditIcon path={'appRankingBanner'} id={row.rankingBannerId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.rankingBannerId}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={APP_RANKING_BANNER_TABLE_COLUMN_COUNT}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
